import { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  TextField,
  LinearProgress,
  Chip,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import PublishIcon from "@mui/icons-material/Publish";
import StopIcon from "@mui/icons-material/Stop";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ReplayIcon from "@mui/icons-material/Replay";
import DownloadIcon from "@mui/icons-material/Download";
import {
  FontTopic,
  DefaultPrimaryButton,
  DefaultSecondaryButton,
} from "@/styles/globalstyles";
import { ConfirmAlert } from "@/utils/alert";
import {
  useExchangeRatePartMigration,
  type ItemStatus,
} from "@/hooks/useExchangeRatePartMigration";
import { pickDirectory } from "@/utils/excel/migrationFileStore";

const STATUS_COLOR: Record<ItemStatus, "success" | "warning" | "error" | "default"> = {
  ok: "success",
  warning: "warning",
  error: "error",
  skipped: "default",
};

type ExchangeRatePartMigrationPanelProps = {
  /** 1 (default) = migrate part ที่ active, 0 = migrate part ที่ปิดไปแล้วในระบบเก่า */
  activeFilter?: 0 | 1;
};

const ExchangeRatePartMigrationPanel = ({
  activeFilter = 1,
}: ExchangeRatePartMigrationPanelProps) => {
  const {
    phase,
    mode,
    currentOp,
    dirName,
    counters,
    log,
    stop,
    setLegacyToken,
    resume,
    resetCheckpoint,
    downloadReport,
    startPull,
    startImport,
  } = useExchangeRatePartMigration(activeFilter);
  const isInactive = activeFilter === 0;

  const [token, setToken] = useState("");
  const [newToken, setNewToken] = useState("");

  const running = phase === "running";
  const needToken = phase === "need-token";
  const progress = counters.total > 0 ? (counters.processed / counters.total) * 100 : 0;

  const pullRunning = running && currentOp === "pull";
  const importRunning = running && currentOp === "import";

  // ─── Phase A handlers ─────────────────────────────────────────────────────────

  const handleStartPull = async () => {
    let dir: FileSystemDirectoryHandle;
    try {
      dir = await pickDirectory();
    } catch {
      return; // user cancelled
    }
    setLegacyToken(token.trim());
    startPull(dir);
  };

  // ─── Phase B handlers ─────────────────────────────────────────────────────────

  const handleStartImport = async (runMode: "dry-run" | "commit") => {
    let dir: FileSystemDirectoryHandle;
    try {
      dir = await pickDirectory();
    } catch {
      return;
    }
    if (runMode === "commit") {
      const confirm = await ConfirmAlert(
        "ยืนยัน Commit",
        "จะ import จริงเข้าระบบใหม่ (เฉพาะ supplier ที่ไม่มี error) — รันบน UAT/PROD ที่ backup แล้วเท่านั้น ดำเนินการต่อ?",
        "Commit",
        "ยกเลิก",
      );
      if (!confirm) return;
    }
    startImport(dir, runMode);
  };

  const handleReset = async () => {
    const confirm = await ConfirmAlert(
      "ล้าง Checkpoint",
      "ล้างความคืบหน้าที่บันทึกไว้ การรันครั้งถัดไปจะเริ่มใหม่ตั้งแต่ต้น ดำเนินการต่อ?",
      "ล้าง",
      "ยกเลิก",
    );
    if (confirm) resetCheckpoint();
  };

  const handleSubmitNewToken = () => {
    if (!newToken.trim()) return;
    setToken(newToken.trim());
    resume(newToken.trim());
    setNewToken("");
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        ...(isInactive && {
          borderColor: "warning.main",
          borderWidth: 2,
          borderLeft: "6px solid",
          borderLeftColor: "warning.main",
        }),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography sx={FontTopic}>
          Migration: Exchange Rate Part (ระบบเก่า)
        </Typography>
        {isInactive && <Chip size="small" color="warning" label="is_active = 0" />}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        ทำงาน 2 phase แยกอิสระ (รายsupplier) — Phase A ดึงไฟล์มาเก็บไว้ในเครื่อง, Phase B อ่านจากโฟลเดอร์นั้นแล้ว import เข้าระบบใหม่
      </Typography>
      {isInactive && (
        <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
          Panel นี้ import เฉพาะ part ที่ปิดไปแล้วในระบบเก่า (is_active = 0) — เลือกโฟลเดอร์คนละอันกับชุด active
          เสมอ เพราะ Phase B อ่านทุกไฟล์ .xlsx ในโฟลเดอร์ที่เลือก ถ้าปนกันไฟล์ของ active จะถูก import เป็น is_active = 0 ไปด้วย
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", md: "row" } }}>

        {/* ── Phase A: ดึง Excel ลงเครื่อง ───────────────────────────────────────── */}
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            p: 2,
            borderColor: pullRunning ? "primary.main" : "divider",
            borderWidth: pullRunning ? 2 : 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <CloudDownloadIcon color="action" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700}>
              Phase A — ดึง Excel ลงเครื่อง
            </Typography>
            {pullRunning && <Chip size="small" color="primary" label="กำลังทำงาน" />}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ต่อ BE local · ใช้ Legacy Token (ตัวเดียวกับ Part Migration) · ไฟล์จะถูกเซฟลงโฟลเดอร์ที่เลือก
            <br />
            รันซ้ำได้ (ไฟล์ที่มีอยู่แล้วจะถูก skip)
          </Typography>

          <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
            ต้องรัน <b>BE local</b> + เชื่อม <code>VITE_API_URL</code> = localhost ก่อนเริ่ม
          </Alert>

          <TextField
            fullWidth
            size="small"
            label="Legacy Token (ระบบเก่า)"
            placeholder="วาง token ที่นี่"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            disabled={pullRunning}
            sx={{ mb: 1.5 }}
          />

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<FolderOpenIcon />}
              sx={DefaultPrimaryButton}
              disabled={running || !token.trim()}
              onClick={handleStartPull}
            >
              เลือกโฟลเดอร์ + เริ่มดึงทั้งหมด
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<StopIcon />}
              onClick={stop}
              disabled={!pullRunning}
            >
              หยุด
            </Button>
          </Box>

          {dirName && currentOp === "pull" && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              โฟลเดอร์: {dirName}
            </Typography>
          )}
        </Paper>

        {/* ── Phase B: นำเข้าระบบใหม่ ──────────────────────────────────────────── */}
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            p: 2,
            borderColor: importRunning ? "secondary.main" : "divider",
            borderWidth: importRunning ? 2 : 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <PublishIcon color="action" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700}>
              Phase B — นำเข้าระบบใหม่
            </Typography>
            {importRunning && (
              <Chip
                size="small"
                color={mode === "commit" ? "primary" : "default"}
                label={mode === "commit" ? "Commit" : "Dry-run"}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ต่อ BE Azure · ไม่ต้องใช้ token · อ่านจากโฟลเดอร์ที่ดึงไว้ใน Phase A
            <br />
            <b>Dry-run</b> = parse อย่างเดียว ·{" "}
            <b>Commit</b> = import จริงเฉพาะ supplier ที่ไม่มี error (atomic ทั้ง supplier)
          </Typography>

          <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
            สลับ <code>VITE_API_URL</code> เป็น <b>Azure</b> + restart dev ก่อนเริ่ม Phase นี้
          </Alert>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<FolderOpenIcon />}
              sx={DefaultSecondaryButton}
              disabled={running}
              onClick={() => handleStartImport("dry-run")}
            >
              เลือกโฟลเดอร์ + Dry-run
            </Button>
            <Button
              variant="contained"
              startIcon={<FolderOpenIcon />}
              sx={DefaultPrimaryButton}
              disabled={running}
              onClick={() => handleStartImport("commit")}
            >
              เลือกโฟลเดอร์ + Commit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<StopIcon />}
              onClick={stop}
              disabled={!importRunning}
            >
              หยุด
            </Button>
          </Box>

          {phase === "done" && currentOp === "import" && counters.error > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<ReplayIcon />}
              onClick={() => resume()}
              sx={{ mt: 1 }}
            >
              Retry errors ({counters.error})
            </Button>
          )}

          {dirName && currentOp === "import" && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              โฟลเดอร์: {dirName}
            </Typography>
          )}
        </Paper>
      </Box>

      {/* ── Token expiry dialog (Phase A เท่านั้น) ──────────────────────────────── */}
      <Dialog open={needToken} maxWidth="sm" fullWidth>
        <DialogTitle>Token ระบบเก่าหมดอายุ</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            หยุดที่ {counters.processed.toLocaleString()}/{counters.total.toLocaleString()}{" "}
            วาง token ใหม่แล้วกด "ทำต่อ" ระบบจะ resume จากจุดเดิม
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Legacy Token ใหม่"
            value={newToken}
            onChange={(e) => setNewToken(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            sx={DefaultPrimaryButton}
            onClick={handleSubmitNewToken}
            disabled={!newToken.trim()}
          >
            ทำต่อ
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Shared: Progress + Log ────────────────────────────────────────────────── */}
      {(running || counters.processed > 0) && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {currentOp === "pull" ? "Phase A — ดึงไฟล์" : "Phase B — นำเข้า"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {counters.processed.toLocaleString()} / {counters.total.toLocaleString()}
            </Typography>
          </Box>

          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mb: 1.5, height: 8, borderRadius: 1 }}
          />

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <Chip size="small" color="success" label={`OK ${counters.ok}`} />
            <Chip size="small" color="error" label={`Error ${counters.error}`} />
            <Chip size="small" label={`Skipped ${counters.skipped}`} />
          </Box>

          {/* ── Report buttons ──────────────────────────────────────────────────── */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={downloadReport}
              disabled={running || counters.processed === 0}
            >
              ดาวน์โหลดรายงาน CSV
            </Button>
            {/* Phase A ใช้ filesystem เป็น checkpoint — localStorage reset ไม่มีผล */}
            {currentOp !== "pull" && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                disabled={running}
              >
                ล้าง Checkpoint
              </Button>
            )}
          </Box>

          {(() => {
            const tableLog = currentOp === "pull" ? log.filter((r) => r.status === "error") : log;
            return tableLog.length > 0 ? (
              <>
                {currentOp === "pull" && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                    แสดงเฉพาะรายการที่ error — กด "ดาวน์โหลดรายงาน CSV" เพื่อดูทั้งหมด
                  </Typography>
                )}
                <TableContainer sx={{ maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Supplier</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Message</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableLog.map((row, i) => (
                        <TableRow key={`${row.supplierCode}-${i}`}>
                          <TableCell>{row.supplierCode}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={STATUS_COLOR[row.status]}
                              label={row.status}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 480,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {row.message}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : null;
          })()}
        </Box>
      )}
    </Paper>
  );
};

export default ExchangeRatePartMigrationPanel;
